import { useState, useEffect, useRef, useMemo } from 'react'
import { Link } from 'react-router-dom'
import Lightbox from './Lightbox'

// Canonical deduplication helper so that different file hashes or duplicate URLs of the same photo never appear twice
function getCanonicalImageKey(url) {
  if (!url) return ''
  const parts = url.split('/')
  const filename = parts[parts.length - 1].split('?')[0].toLowerCase()
  // Strip trailing hash e.g. _25eccdb1 or _4efa9672 or _a63d675a
  return filename.replace(/_[a-f0-9]{8,}(?=\.[a-z0-9]+$)/i, '')
}

// Pool of scraped authentic party, DJ and event photos (cleaned & canonical)
const RAW_PHOTO_POOL = [
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/selects-00-33-10-15-still012.jpg',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-8471.jpg',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-0003.jpg',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-9394.jpg',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-0027.jpg',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-8940.jpg',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/casperdj0184-ricofeldfoss-1.jpg',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-3466.jpg',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/img-1567.jpg',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/sk-rmbillede-2022-01-05-155014.png',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/selects-01-45-15-03-still004.jpg',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/koldt.png',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/28208-a.jpg',
  'https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/image-asset.jpeg',
]

// Deduplicate helper ensuring strictly one URL per canonical photo
function deduplicateImages(list) {
  const seen = new Set()
  const result = []
  for (const item of list) {
    if (!item) continue
    const key = getCanonicalImageKey(item)
    if (!seen.has(key)) {
      seen.add(key)
      result.push(item)
    }
  }
  return result
}

const DISTINCT_BASE_POOL = deduplicateImages(RAW_PHOTO_POOL)

// Helper to optimize Auxo asset images using Cloudflare CDN
function getOptimizedImageUrl(url, width = 800) {
  if (!url || !url.includes('assets.auxodev.dk')) return url
  if (url.includes('/cdn-cgi/image/')) return url
  return url.replace(
    'https://assets.auxodev.dk/',
    `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=80,format=auto/`
  )
}

export default function GallerySection({
  gallerySlug = 'galleri-billeder',
  title = 'Galleri',
  subtitle,
  ctaText = 'Se hele galleriet',
  ctaLink = '/galleri',
}) {
  const [activeIdx, setActiveIdx] = useState(null)
  const [isHovered, setIsHovered] = useState(false)
  const [cmsImages, setCmsImages] = useState([])
  const lastFlippedIdx = useRef(-1)

  // Fetch any additional images from CMS gallery if available
  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/galleries')
      .then((res) => (res.ok ? res.json() : []))
      .then((items) => {
        if (Array.isArray(items)) {
          const allImgs = items.flatMap((item) => (Array.isArray(item.images) ? item.images : []))
          if (allImgs.length > 0) {
            setCmsImages(allImgs)
          }
        }
      })
      .catch(() => {})
  }, [])

  // Full pool of available images, deduplicated strictly by canonical photo identity
  const pool = useMemo(() => {
    return deduplicateImages([...DISTINCT_BASE_POOL, ...cmsImages])
  }, [cmsImages])

  // Initialize 9 slots for the 3x3 grid — all 9 initial tiles show strictly unique images
  const [slots, setSlots] = useState(() => {
    return Array.from({ length: 9 }, (_, i) => ({
      frontImage: DISTINCT_BASE_POOL[i] || DISTINCT_BASE_POOL[0],
      backImage: DISTINCT_BASE_POOL[(i + 9) % DISTINCT_BASE_POOL.length] || DISTINCT_BASE_POOL[0],
      isFlipped: false,
    }))
  })

  // Periodically flip a random tile to another image from the pool, GUARANTEEING no duplicate image is ever shown on screen
  useEffect(() => {
    if (pool.length <= 9) return

    const timer = setInterval(() => {
      // Pause flipping if the user is hovering over the gallery or has the lightbox open
      if (isHovered || activeIdx !== null) return

      setSlots((prevSlots) => {
        // Pick a random tile index that wasn't the exact one flipped previously
        const availableIndices = [0, 1, 2, 3, 4, 5, 6, 7, 8].filter(
          (idx) => idx !== lastFlippedIdx.current
        )
        const targetIdx =
          availableIndices[Math.floor(Math.random() * availableIndices.length)]
        lastFlippedIdx.current = targetIdx

        // Determine the canonical keys of images currently visible on all OTHER 8 tiles
        const visibleOnOtherTiles = prevSlots
          .filter((_, idx) => idx !== targetIdx)
          .map((s) => (s.isFlipped ? s.backImage : s.frontImage))
        const otherKeys = new Set(visibleOnOtherTiles.map(getCanonicalImageKey))

        // Canonical key of the image currently shown on the target tile
        const currentTargetImage = prevSlots[targetIdx].isFlipped
          ? prevSlots[targetIdx].backImage
          : prevSlots[targetIdx].frontImage
        const currentTargetKey = getCanonicalImageKey(currentTargetImage)

        // Find images in pool that are NOT visible on ANY other tile AND different from this tile's current photo
        const eligibleCandidates = pool.filter((img) => {
          const key = getCanonicalImageKey(img)
          return !otherKeys.has(key) && key !== currentTargetKey
        })

        // Safety fallback: strictly never collide with other tiles
        const fallbackCandidates = pool.filter((img) => !otherKeys.has(getCanonicalImageKey(img)))
        const finalCandidatePool =
          eligibleCandidates.length > 0
            ? eligibleCandidates
            : fallbackCandidates.length > 0
            ? fallbackCandidates
            : pool

        const nextImage =
          finalCandidatePool[Math.floor(Math.random() * finalCandidatePool.length)]

        return prevSlots.map((slot, idx) => {
          if (idx !== targetIdx) return slot

          if (slot.isFlipped) {
            // Card is currently showing back; prepare new unique image on front, then flip to front
            return {
              ...slot,
              frontImage: nextImage,
              isFlipped: false,
            }
          } else {
            // Card is currently showing front; prepare new unique image on back, then flip to back
            return {
              ...slot,
              backImage: nextImage,
              isFlipped: true,
            }
          }
        })
      })
    }, 2800)

    return () => clearInterval(timer)
  }, [pool, isHovered, activeIdx])

  // Get currently visible image list for lightbox
  const currentImages = slots.map((s) => (s.isFlipped ? s.backImage : s.frontImage))

  return (
    <section className="relative py-20 md:py-28 bg-neutral-950 text-white overflow-hidden border-t border-neutral-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Centered Header Block */}
        {(title || subtitle) && (
          <div className="mb-12 md:mb-16 text-center max-w-2xl mx-auto">
            <span className="text-gold-400 text-xs tracking-widest uppercase font-semibold">
              Stemning &amp; Indtryk
            </span>
            {title && (
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white uppercase mt-2">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-neutral-400 text-sm sm:text-base mt-3 leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {/* 3x3 Grid Wrapper with Random Smooth 3D Flip */}
        <div
          className="relative max-w-4xl mx-auto"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* 3x3 Square Image Grid: zero-radius sharp corners, thin gap */}
          <div className="grid grid-cols-3 gap-1.5 sm:gap-2 relative z-10 shadow-2xl">
            {slots.map((slot, i) => (
              <div
                key={i}
                onClick={() => setActiveIdx(i)}
                className="group relative aspect-square w-full rounded-none bg-neutral-900 border border-neutral-800/80 hover:border-gold-400/80 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-gold-400 cursor-pointer overflow-hidden"
                style={{ perspective: '1000px' }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setActiveIdx(i)
                  }
                }}
                aria-label={`Åbn galleribillede ${i + 1}`}
              >
                {/* 3D Flipping Card Container */}
                <div
                  className="relative w-full h-full transition-transform duration-700 ease-in-out"
                  style={{
                    transformStyle: 'preserve-3d',
                    WebkitTransformStyle: 'preserve-3d',
                    transform: slot.isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                  }}
                >
                  {/* Front Face */}
                  <div
                    className="absolute inset-0 w-full h-full overflow-hidden bg-neutral-900"
                    style={{
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                    }}
                  >
                    <img
                      src={getOptimizedImageUrl(slot.frontImage, 600)}
                      alt={`Casper DJ festbillede ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-3">
                      <span className="w-9 h-9 rounded-full bg-neutral-950/80 border border-gold-400/60 text-gold-400 flex items-center justify-center shadow-lg">
                        <i className="fa-solid fa-magnifying-glass text-xs" aria-hidden="true" />
                      </span>
                    </div>
                  </div>

                  {/* Back Face */}
                  <div
                    className="absolute inset-0 w-full h-full overflow-hidden bg-neutral-900"
                    style={{
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                    }}
                  >
                    <img
                      src={getOptimizedImageUrl(slot.backImage, 600)}
                      alt={`Casper DJ festbillede ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-3">
                      <span className="w-9 h-9 rounded-full bg-neutral-950/80 border border-gold-400/60 text-gold-400 flex items-center justify-center shadow-lg">
                        <i className="fa-solid fa-magnifying-glass text-xs" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Centered Pill-Shaped Primary CTA Button */}
        <div className="mt-10 sm:mt-12 flex flex-col items-center justify-center gap-4 text-center">
          <Link
            to={ctaLink}
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-gold-400 text-neutral-950 hover:bg-gold-300 transition-all duration-200 font-bold text-xs uppercase tracking-widest shadow-xl hover:shadow-gold-400/20 active:scale-95 cursor-pointer"
          >
            <span>{ctaText}</span>
            <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true" />
          </Link>
        </div>
      </div>

      {/* Lightbox Modal */}
      {activeIdx !== null && (
        <Lightbox
          images={currentImages}
          currentIndex={activeIdx}
          onClose={() => setActiveIdx(null)}
          onPrev={() => setActiveIdx((prev) => (prev > 0 ? prev - 1 : currentImages.length - 1))}
          onNext={() => setActiveIdx((prev) => (prev < currentImages.length - 1 ? prev + 1 : 0))}
        />
      )}
    </section>
  )
}
